import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from './ui/card';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireEmployeeAccess?: boolean;
}

export function ProtectedRoute({
  children,
  requireEmployeeAccess = true,
}: ProtectedRouteProps) {
  const { user, hasEmployeeAccess, loading, profileLoading } = useAuth();
  const location = useLocation();

  // Redirect to login if not authenticated (skip loading state to show page skeleton)
  if (!loading && !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check employee access if required (only after loading is complete)
  // Note: hasEmployeeAccess will be false until profile loads, but we now
  // handle this in AuthContext by keeping loading=true until access is checked
  // profileLoading counts as loading: access is unknown until the profile
  // arrives, so don't flash "Access Denied" right after login
  if (
    !loading &&
    !profileLoading &&
    requireEmployeeAccess &&
    !hasEmployeeAccess
  ) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <Card className="max-w-md mx-auto">
          <CardContent className="p-6 text-center">
            <div className="mb-4">
              <svg
                className="mx-auto h-12 w-12 text-red-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-stone-900 mb-2">
              Access Denied
            </h3>
            <p className="text-stone-600 mb-4">
              You don't have permission to access the employee portal. Please
              contact your administrator to request access.
            </p>
            <button
              onClick={() => (window.location.href = '/')}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-all"
            >
              Return to Main Site
            </button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
}
